import {
  CONDITION_LIMITS, CONDITIONAL_RULES_CAPABILITY, validateNativePredicate,
  type ConditionalPredicate, type ConditionalRules, type ConditionalScalar,
  type ConditionalScope, type ConditionalStyleOverrides, type ConditionalValueType,
} from "../nativeConditions.mjs";
import { asArray, asRecord, cloneValue, type DesignerDocument, type DesignerSelection, type JsonRecord } from "./reportDesignerModel";

type Path = (string | number)[];
type StyleKey = keyof ConditionalStyleOverrides;
export const CONDITIONAL_STYLE_LABELS: Record<StyleKey, string> = {
  color: "Text color", backgroundColor: "Background color", bold: "Bold",
  italic: "Italic", borderColor: "Border color", borderWidth: "Border width",
};
export type ConditionalTarget = {
  key: string; label: string; path: Path; node: JsonRecord; scopes: ConditionalScope[];
  styleKeys: StyleKey[]; datasetId?: string; table?: JsonRecord;
  summaryCell?: boolean; column?: JsonRecord;
};
export type ConditionalDataset = {
  id: string; label: string; kind: "collection" | "single" | "unknown";
  fields: { path: string; label: string; type: string; metadata?: JsonRecord }[];
};
export type ConditionalFieldOption = {
  key: string; label: string; scalar: ConditionalScalar; type?: ConditionalValueType;
  enumValues?: string[]; nullable?: boolean;
};
export type ConditionalRuleAction =
  | { type: "addVisibility"; when: ConditionalPredicate }
  | { type: "addFormatting"; when: ConditionalPredicate; style: ConditionalStyleOverrides }
  | { type: "update"; id: string; when?: ConditionalPredicate; style?: ConditionalStyleOverrides; enabled?: boolean }
  | { type: "remove" | "duplicate"; id: string }
  | { type: "move"; id: string; direction: -1 | 1 };

const textStyleKeys = Object.keys(CONDITIONAL_STYLE_LABELS) as StyleKey[];
const boxStyleKeys: StyleKey[] = ["backgroundColor", "borderColor", "borderWidth"];

export function conditionalRules(node: JsonRecord): ConditionalRules {
  // Native JSON is admitted by the canonical validator before reaching authoring.
  return asRecord(node.conditions) as ConditionalRules;
}

export function conditionalTargets(layout: JsonRecord, selection: DesignerSelection): ConditionalTarget[] {
  if (!("bandId" in selection)) return [];
  const bands = asArray(layout.bands).map(asRecord);
  const bandIndex = bands.findIndex(band => band.id === selection.bandId);
  const band = bands[bandIndex];
  if (!band || !["reportHeader", "pageHeader", "detail", "reportFooter", "pageFooter"].includes(String(band.role))) return [];
  const bandScopes: ConditionalScope[] = band.role === "detail" && band.dataset ? ["document", "detail"] : ["document"];
  const base: Path = ["bands", bandIndex];
  const targets: ConditionalTarget[] = [];
  const sourceId = selection.type === "nativeElement" ? selection.elementIds[0] :
    selection.type === "nativeTableColumn" || selection.type === "nativeTableGroup" ? selection.tableId : null;
  function add(label: string, path: Path, scopes: ConditionalScope[], styleKeys: StyleKey[], extra: Partial<ConditionalTarget> = {}) {
    const occurrence = targets.filter(target => target.label === label).length;
    const key = JSON.stringify([band.id, sourceId, selection.type === "nativeTableColumn" ? selection.columnId : null, label, occurrence]);
    targets.push({ key, label, path, node: nodeAt(layout, path), scopes, styleKeys, ...extra });
  }
  if (selection.type === "band") {
    add("Band", base, bandScopes, [], { datasetId: String(band.dataset || "") });
    return targets;
  }
  const elementId = selection.type === "nativeElement" ? selection.elementIds.length === 1 ? selection.elementIds[0] : "" :
    selection.type === "nativeTableColumn" || selection.type === "nativeTableGroup" ? selection.tableId : "";
  function find(elements: unknown, parent: Path, depth = 0): { element: JsonRecord; path: Path; depth: number } | undefined {
    for (const [index, element] of asArray(elements).map(asRecord).entries()) {
      const path = [...parent, "elements", index];
      if (element.id === elementId) return { element, path, depth };
      if (element.type === "container") {
        const nested = find(element.elements, path, depth + 1);
        if (nested) return nested;
      }
    }
    return undefined;
  }
  const found = find(band.elements, base);
  if (!found) return [];
  const { element, path } = found;
  if (element.type !== "table") {
    if (["text", "field", "container", "image"].includes(String(element.type))) {
      add("Element", path, bandScopes, ["image", "container"].includes(String(element.type)) ? boxStyleKeys : textStyleKeys, { datasetId: String(band.dataset || "") });
    }
    return targets;
  }
  if (found.depth) return [];
  const tableScopes: ConditionalScope[] = ["document", "dataset"];
  const group = asArray(element.groups).map(asRecord)[0];
  const rowScopes: ConditionalScope[] = [...tableScopes, ...(group ? ["group" as const] : []), "detail"];
  const common = { datasetId: String(element.dataset || ""), table: element };
  if (selection.type === "nativeTableColumn") {
    const index = asArray(element.columns).map(asRecord).findIndex(column => column.id === selection.columnId);
    if (index < 0) return [];
    const column = asRecord(asArray(element.columns)[index]);
    add("Whole column - visibility", [...path, "columns", index], tableScopes, [], { ...common, column });
    add("Detail cell", [...path, "columns", index, "detail"], [...rowScopes, "cell"], textStyleKeys, { ...common, column });
    return targets;
  }
  const region = selection.type === "nativeTableGroup" ? selection.region : undefined;
  if (!region || region === "group") add("Whole table - visibility", path, tableScopes, [], common);
  if (!region) add("Table heading", [...path, "header"], tableScopes, textStyleKeys, common);
  if (!region || region === "detail") add("Detail row", [...path, "row"], rowScopes, textStyleKeys, common);
  if (group && (!region || region === "header" || region === "group")) add("Group heading", [...path, "groups", 0, "header"], [...tableScopes, "group"], textStyleKeys, common);
  function summary(label: string, summaryPath: Path, scopes: ConditionalScope[]) {
    add(label, summaryPath, scopes, textStyleKeys, common);
    asArray(nodeAt(layout, summaryPath).cells).map(asRecord).forEach((cell, index) => {
      add(`${label} cell: ${String(cell.label || cell.text || cell.columnId || index + 1)}`, [...summaryPath, "cells", index], [...scopes, "cell"], textStyleKeys, { ...common, summaryCell: true, column: asArray(element.columns).map(asRecord).find(column => column.id === cell.columnId) });
    });
  }
  if (group && (!region || region === "footer" || region === "group")) summary("Group subtotal", [...path, "groups", 0, "footer"], [...tableScopes, "group"]);
  if (!region || region === "tableFooter") summary("Table total", [...path, "footer"], tableScopes);
  return targets;
}

function nodeAt(layout: JsonRecord, path: Path, create = false): JsonRecord {
  let node: unknown = layout;
  for (const key of path) {
    if (!node || typeof node !== "object") return {};
    if (Array.isArray(node)) node = typeof key === "number" ? node[key] : undefined;
    else {
      const record = asRecord(node);
      if (record[key] === undefined && create) record[key] = {};
      node = record[key];
    }
  }
  return asRecord(node);
}

export function nextConditionalIdentity(layout: JsonRecord, prefix = "condition"): string {
  const ids = conditionalIdentities(layout);
  let index = 1;
  while (ids.has(`${prefix}-${index}`)) index++;
  return `${prefix}-${index}`;
}

function conditionalIdentities(value: unknown, ids = new Set<string>()): Set<string> {
  if (!value || typeof value !== "object") return ids;
  if (typeof asRecord(value).id === "string") ids.add(String(asRecord(value).id));
  Object.values(value).forEach(child => conditionalIdentities(child, ids));
  return ids;
}

export function reidentifyConditionalCopy(layout: JsonRecord, copy: JsonRecord, descendants = false) {
  const reserved = conditionalIdentities(copy, conditionalIdentities(layout));
  function next(prefix: string) {
    let index = 1;
    while (reserved.has(`${prefix}-${index}`)) index++;
    const id = `${prefix}-${index}`;
    reserved.add(id);
    return id;
  }
  function visit(value: unknown, parentKey = "", root = false) {
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) { value.forEach(child => visit(child, parentKey)); return; }
    const node = asRecord(value);
    if (descendants && !root && ["text", "field", "container", "image", "table"].includes(String(node.type))) node.id = next("conditional-element");
    const conditions = conditionalRules(node);
    if (conditions.visibleWhen) conditions.visibleWhen.id = next("condition");
    conditions.formatting?.forEach(rule => { rule.id = next("condition"); });
    if (parentKey === "cells" && node.conditions) node.id = next("conditional-cell");
    Object.entries(node).forEach(([key, child]) => { if (key !== "conditions") visit(child, key); });
  }
  visit(copy, "", true);
}

export function mutateConditionalRule(document: DesignerDocument, selection: DesignerSelection, targetKey: string, action: ConditionalRuleAction) {
  const layout = document.renderer.layout;
  const target = conditionalTargets(layout, selection).find(candidate => candidate.key === targetKey);
  if (!target) throw new Error("Select a supported single conditional target.");
  const node = nodeAt(layout, target.path, true);
  const conditions = cloneValue(conditionalRules(node));
  const formatting = conditions.formatting || [];
  const rule = conditions.visibleWhen?.id === ("id" in action ? action.id : undefined)
    ? conditions.visibleWhen : formatting.find(candidate => "id" in action && candidate.id === action.id);
  if ("id" in action && !rule) throw new Error("This rule no longer exists. Select it again.");
  if (action.type === "addVisibility") {
    if (conditions.visibleWhen) throw new Error("This target already has a visibility rule. Edit or remove it first.");
    conditions.visibleWhen = { id: nextConditionalIdentity(layout), when: cloneValue(action.when) };
  } else if (action.type === "addFormatting") {
    if (!target.styleKeys.length) throw new Error("This target supports visibility only.");
    if (formatting.length >= CONDITION_LIMITS.rulesPerTarget) throw new Error(`A target supports at most ${CONDITION_LIMITS.rulesPerTarget} formatting rules.`);
    conditions.formatting = [...formatting, { id: nextConditionalIdentity(layout), when: cloneValue(action.when), style: cloneValue(action.style) }];
  } else if (action.type === "remove") {
    if (conditions.visibleWhen?.id === action.id) delete conditions.visibleWhen;
    else conditions.formatting = formatting.filter(candidate => candidate.id !== action.id);
  } else if (action.type === "duplicate") {
    const source = formatting.find(candidate => candidate.id === action.id);
    if (!source) throw new Error("Visibility has one canonical rule per target; only formatting rules can be duplicated.");
    const index = formatting.indexOf(source);
    formatting.splice(index + 1, 0, { ...cloneValue(source), id: nextConditionalIdentity(layout) });
    conditions.formatting = formatting;
  } else if (action.type === "move") {
    const index = formatting.findIndex(candidate => candidate.id === action.id);
    if (index < 0 || index + action.direction < 0 || index + action.direction >= formatting.length) throw new Error("The rule cannot move beyond this list.");
    [formatting[index], formatting[index + action.direction]] = [formatting[index + action.direction], formatting[index]];
    conditions.formatting = formatting;
  } else if (action.type === "update" && rule) {
    if (action.when !== undefined) rule.when = cloneValue(action.when);
    if (action.enabled !== undefined) rule.enabled = action.enabled;
    if (action.style !== undefined) {
      if (!("style" in rule)) throw new Error("Visibility rules cannot override styles.");
      rule.style = cloneValue(action.style);
    }
  }
  if (conditions.formatting?.length === 0) delete conditions.formatting;
  if (conditions.visibleWhen || conditions.formatting?.length) {
    node.conditions = conditions;
    if (target.summaryCell && !node.id) node.id = nextConditionalIdentity(layout, "conditional-cell");
    layout.requiredFeatures = [...new Set([...asArray(layout.requiredFeatures), CONDITIONAL_RULES_CAPABILITY])];
  } else delete node.conditions;
}

export function conditionalFieldOptions(target: ConditionalTarget, datasets: ConditionalDataset[]): ConditionalFieldOption[] {
  const options: ConditionalFieldOption[] = [];
  const selected = datasets.find(dataset => dataset.id === target.datasetId);
  function add(scalar: ConditionalScalar, label: string, type?: ConditionalValueType, metadata?: JsonRecord) {
    const enumValues = type === "string" ? asArray(metadata?.enum).filter((value): value is string => typeof value === "string") : [];
    const nullable = typeof metadata?.nullable === "boolean" ? metadata.nullable : undefined;
    options.push({ key: scalarIdentity(scalar), scalar, label: `${label} (${type || "unknown type"}${nullable ? ", nullable" : ""}${enumValues.length ? `, enum: ${enumValues.join(" | ")}` : ""})`, type,
      ...(enumValues.length ? { enumValues } : {}), ...(nullable === undefined ? {} : { nullable }) });
  }
  function fields(dataset: ConditionalDataset, scope: ConditionalScope, prefix = "") {
    dataset.fields.forEach(field => {
      const type = conditionalFieldType(field.type);
      const scalar: ConditionalScalar = { field: `${prefix}${field.path}`, scope, ...(type ? { valueType: type } : {}) };
      add(scalar, `${dataset.label} / ${field.label} - ${scope}`, type, field.metadata);
    });
  }
  if (target.scopes.includes("document")) datasets.filter(dataset => dataset.kind === "single").forEach(dataset => fields(dataset, "document", `${dataset.id}.`));
  if (selected && target.scopes.includes("detail")) {
    fields(selected, "detail");
    asArray(target.table?.columns).map(asRecord).filter(column => column.expr || column.calculation).forEach(column => {
      add({ field: String(column.id), scope: "detail", valueType: "number" }, `Calculated: ${String(column.label || column.id)}`, "number");
    });
  }
  if (selected && target.scopes.includes("dataset")) {
    // Collection fields are row fields, not properties of an array/wrapper.
    if (selected.kind === "single") fields(selected, "dataset");
    else selected.fields.filter(field => field.metadata?.scope === "dataset").forEach(field => {
      const type = conditionalFieldType(field.type);
      add({ field: field.path, scope: "dataset", ...(type ? { valueType: type } : {}) }, `${selected.label} / ${field.label} - dataset`, type);
    });
  }
  if (target.scopes.includes("group")) {
    const group = asRecord(asArray(target.table?.groups)[0]);
    const field = String(group.field || asRecord(group.key).field || "");
    const type = conditionalFieldType(selected?.fields.find(candidate => candidate.path === field)?.type || "");
    add({ field: "key", scope: "group", ...(type ? { valueType: type } : {}) }, "Current group key", type);
  }
  if (target.scopes.includes("cell")) {
    const column = target.column;
    const type = column?.expr || column?.calculation || (target.summaryCell && asRecord(target.node.aggregate).fn)
      ? "number" : conditionalFieldType(selected?.fields.find(field => field.path === column?.binding)?.type || "");
    add({ field: "value", scope: "cell", ...(type ? { valueType: type } : {}) }, "Current cell value", type);
  }
  if (target.table && target.scopes.includes("dataset")) {
    const numeric = [
      ...(selected?.fields.filter(field => conditionalFieldType(field.type) === "number").map(field => ({ id: field.path, label: field.label })) || []),
      ...asArray(target.table.columns).map(asRecord).filter(column => column.expr || column.calculation).map(column => ({ id: String(column.id), label: `Calculated: ${String(column.label || column.id)}` })),
    ];
    const scopes = target.scopes.includes("group") ? ["report", "group"] as const : ["report"] as const;
    scopes.forEach(scope => {
      add({ aggregate: { fn: "count", scope } }, `${scope} record count`, "number");
      numeric.forEach(field => (["sum", "min", "max", "avg"] as const).forEach(fn => {
        add({ aggregate: { fn, scope, field: field.id } }, `${scope} ${fn}: ${field.label}`, "number");
      }));
    });
  }
  return options.filter((option, index) => options.findIndex(candidate => candidate.key === option.key) === index);
}

export function conditionalFieldType(type: string): ConditionalValueType | undefined {
  const types: Record<string, ConditionalValueType> = {
    number: "number", integer: "number", decimal: "number", double: "number",
    string: "string", boolean: "boolean", date: "date", timestamp: "timestamp",
  };
  return types[type.toLowerCase()];
}

export function scalarIdentity(scalar: ConditionalScalar): string {
  return "field" in scalar ? `${scalar.scope}:${scalar.field}` : JSON.stringify(scalar);
}

export function defaultConditionalPredicate(fields: ConditionalFieldOption[]): ConditionalPredicate {
  const field = fields[0];
  return !field ? { const: true } : !field.type ? { op: "isNotNull", arg: cloneValue(field.scalar) } :
    { op: "eq", left: cloneValue(field.scalar), right: field.enumValues?.length ? { const: field.enumValues[0] } : defaultConditionalLiteral(field.type) };
}

export function defaultConditionalLiteral(type?: ConditionalValueType): ConditionalScalar {
  return type === "date" || type === "timestamp" ? { const: "", valueType: type } :
    { const: type === "number" ? 0 : type === "boolean" ? false : "" };
}

export function conditionalPredicateErrors(predicate: ConditionalPredicate, target: ConditionalTarget, fields: ConditionalFieldOption[]): string[] {
  const declared: Partial<Record<ConditionalScope, Record<string, ConditionalValueType>>> = {};
  fields.forEach(field => {
    if ("field" in field.scalar && field.type) {
      const scalar = field.scalar;
      const scope = declared[scalar.scope] || {};
      scope[scalar.field] = field.type;
      declared[scalar.scope] = scope;
    }
  });
  const errors = validateNativePredicate(predicate, { allowedScopes: target.scopes, fields: declared }).errors.map(error => error.message);
  function scalar(value: ConditionalScalar) {
    if ("const" in value) return;
    if (!fields.some(field => field.key === scalarIdentity(value))) errors.push("This binding is unresolved or unavailable in the selected scope. It has been preserved; select an available field to replace it.");
  }
  function visit(value: ConditionalPredicate) {
    if ("const" in value) return;
    if (value.op === "and" || value.op === "or") value.args.forEach(visit);
    else if (value.op === "not") visit(value.arg);
    else if ("left" in value) {
      scalar(value.left); scalar(value.right);
      const field = fields.find(field => field.key === scalarIdentity(value.left));
      if (field?.type === "number" && "const" in value.right && value.right.const === "") errors.push("Enter a numeric comparison value. Empty input is not zero or null.");
    }
    else if ("arg" in value) scalar(value.arg);
  }
  visit(predicate);
  return [...new Set(errors)];
}

export function conditionalSummary(predicate: ConditionalPredicate, fields: ConditionalFieldOption[]): string {
  function scalar(value: ConditionalScalar): string {
    if ("const" in value) return value.const === null ? "null" : typeof value.const === "string" ? `"${value.const}"` : String(value.const);
    return fields.find(field => field.key === scalarIdentity(value))?.label.replace(/ \([^)]*\)$/, "") ||
      ("field" in value ? `${value.scope}: ${value.field}` : `${value.aggregate.scope} ${value.aggregate.fn}`);
  }
  if ("const" in predicate) return predicate.const ? "Always" : "Never";
  if (predicate.op === "and" || predicate.op === "or") return `(${predicate.args.map(arg => conditionalSummary(arg, fields)).join(predicate.op === "and" ? " AND " : " OR ")})`;
  if (predicate.op === "not") return `NOT ${conditionalSummary(predicate.arg, fields)}`;
  if (predicate.op === "isNull" || predicate.op === "isNotNull") return `${scalar(predicate.arg)} ${predicate.op === "isNull" ? "is null" : "is not null"}`;
  if ("left" in predicate) {
    const labels = { eq: "equals", neq: "does not equal", gt: "exceeds", gte: "is at least", lt: "is below", lte: "is at most" };
    return `${scalar(predicate.left)} ${labels[predicate.op]} ${scalar(predicate.right)}`;
  }
  return "Unsupported condition";
}
